import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import {
    useCurrentModelChange,
    useCurrentMedia,
    useFetchComparisonMetrics,
    useComparisonLoading,
    useMetricsLoading,
    useModelPageState
} from "@/store";
import { ModelPageState } from "./types";
import { SidebarHeader } from "@/components/layout/SidebarHeader";
import { FileCarousel, FileCarouselLayout } from "@/components/file/Carousel/FileCarousel";
import { FilePreviewProvider } from "@/contexts/FilePreviewProvider";
import { ModelComparisonInsights } from "./components/ModelComparisonInsights";
import { FileInputArea } from "@/components/flow/shared/FileInputArea";
import { ComparisonPreview } from "./components/ComparisonPreview";
import { ModelComparisonProvider, useModelComparison } from "./contexts";
import { ModelLayout } from "./Layout";

function ModelComparisonContent() {
    const currentChange = useCurrentModelChange();
    const currentMedia = useCurrentMedia();
    const fetchComparisonMetrics = useFetchComparisonMetrics();
    const comparisonLoading = useComparisonLoading();
    const { isLoadingSources, handleUploadedFiles } = useModelComparison();
    const modelPageState = useModelPageState();

    const isComparisonMode = modelPageState === ModelPageState.NEW_MODEL_COMPARISON;
    const metricsLoading = useMetricsLoading();

    // Show loading spinner while fetching sources from API or downloading images into media system
    const isLoadingTestSources = comparisonLoading || isLoadingSources;

    // Fetch comparison metrics when the component mounts
    useEffect(() => {
        if (currentChange) {
            fetchComparisonMetrics();
        }
    }, [currentChange, fetchComparisonMetrics]);

    // Handle file uploads: add to media system and run model inference
    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            try {
                await handleUploadedFiles(files);
            } finally {
                onFinally?.();
            }
        },
        [handleUploadedFiles]
    );

    if (!currentChange) {
        return null;
    }

    const Sidebar = (
        <>
            <SidebarHeader
                title={isComparisonMode ? "New Model Available" : "Model Performance"}
                description={
                    isComparisonMode
                        ? "Your model has been retrained with new data. Review the changes below."
                        : "Review your model's performance on test images."
                }
            />
            <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-hidden">
                <ModelComparisonInsights />
                <AnimatePresence>
                    {!metricsLoading && (
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            className={twClassNames(
                                isComparisonMode ? "shrink-0" : "flex min-h-0 flex-1"
                            )}
                        >
                            <FileInputArea
                                addMediaItems={handleUploadedFiles}
                                showAllMethods={false}
                                imagesOnly={true}
                                className={twClassNames(
                                    "py-0 [&_h2]:text-sm [&_h2]:sm:text-base",
                                    isComparisonMode && "!h-auto !flex-none"
                                )}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );

    const VisualArea = (
        <FilePreviewProvider>
            <AnimatePresence mode="wait">
                <div className="visualArea">
                    <div className="flex min-h-0 flex-1">
                        <FileCarousel
                            onAddMediaItems={onAddMediaItems}
                            layout={FileCarouselLayout.MODEL}
                            isComparisonMode={true} // Always true on model page to hide header/upload container
                            isLoadingRandomSource={isLoadingTestSources}
                        >
                            <ComparisonPreview
                                key={`comparison-${currentMedia?.file?.id}`}
                                newModelRef={currentChange.to?.ref}
                                oldModelRef={isComparisonMode ? currentChange.from?.ref : undefined}
                            />
                        </FileCarousel>
                    </div>
                </div>
            </AnimatePresence>
        </FilePreviewProvider>
    );

    return <ModelLayout Sidebar={Sidebar} VisualArea={VisualArea} />;
}

export function ModelComparisonView() {
    return (
        <ModelComparisonProvider>
            <ModelComparisonContent />
        </ModelComparisonProvider>
    );
}
